// EMAIL-CRM-2026-10-22: every email the CRM sends is a real send through crm-mailer -- from Matt, Reply-To Matt, his
// signature appended by the mailer -- and none opens a mail app any more (no mailto anywhere).
//
//   Browser -> POST /api/send-email { to, kind, body, subject? } (same origin + session + staff allowlist)
//           -> crm-mailer /send (checks the session again, sends through Cloudflare Email Sending).
//   kind 'exam' | 'onboarding': a portal link must be in the body (the mailer refuses one without: no_portal_link).
//   kind 'message': anything else -- a lead email, a facility reply, an exam result -- with a subject (<= 150).
//
// The mailer answers synchronously and writes nothing, so the CRM records the outcome itself: one activity_log row,
// type 'email', note "Email · <subject> · Sent" (or Bounced), " · lead_id=<uuid>" when it went to a lead -- the
// timeline finds a lead's rows by that id and a candidate's by candidate_id. The note never holds the address or body.
//
// A 409 recipient_suppressed is a hard bounce or a complaint: Cloudflare refuses the address for good, so the CRM logs
// Bounced and sets leads.email_opt_out on every lead with that address. Until the hub applies the agents window's
// 2026-10-22-leads-email.sql the column does not exist: reads treat anything but true as "not opted out" and the write
// is skipped (said, never fatal).

export const EMAIL_LOG_TYPE = 'email';
export const SUBJECT_MAX = 150;
export const BODY_MAX = { message: 4000, exam: 2000, onboarding: 2000 };
export const STATUS = { sent: 'Sent', bounced: 'Bounced', optedOut: 'Opted out' };
export const NO_EMAIL_WHY = 'No email: they opted out or their address bounced, so Email is off.';
export const FROM_LINE = 'From Matt Michuda. Replies go to Matt’s inbox; his signature is added at the end.';

export const EMAIL_ERRORS = {
  recipient_suppressed: 'Bounced: this address refuses mail (a bounce or a complaint). It is now marked No email.',
  bad_address: 'That email address is not valid.',
  empty_body: 'Write a message first.',
  body_too_long: 'The message is too long for one email. Shorten it and send again.',
  empty_subject: 'Add a subject first.',
  subject_too_long: `The subject is too long (${SUBJECT_MAX} characters at most).`,
  no_portal_link: 'The link is missing from the message. Put it back and send again.',
  bad_kind: 'This kind of email cannot be sent.',
  rate_limited: 'Too many emails just now. Wait a minute and send again.',
  email_not_ready: 'The company email is not available right now. Try again in a few minutes.',
  send_failed: 'The email did not go out. Send again.',
  upstream: 'The email did not go out (the mailer could not be reached). Send again.',
  unauthorized: 'Your sign-in has expired. Sign in again, then send.',
  forbidden: 'This page cannot send email. Reload the CRM and try again.',
};

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const lower = (v) => s(v).toLowerCase();
const oneLine = (v) => s(v).replace(/[\r\n·]+/g, ' ').replace(/\s+/g, ' ').trim();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Anything but true is "not opted out" (the column may not exist yet). */
export const isOptedOut = (row) => !!row && row.email_opt_out === true;

/** The leads with this address (case-insensitive). */
export const leadsWithAddress = (address, leads) => {
  const a = lower(address);
  return a ? (leads || []).filter((l) => l && lower(l.email) === a) : [];
};

// The agents window records an address that refuses mail before the column exists: decision rows on the lead
// (email_suppressed = a bounce or complaint at their first email; email_unsubscribed = the job-alert digest's link).
export const OPT_OUT_DECISIONS = new Set(['email_suppressed', 'email_unsubscribed']);
/** decisions: Map(leadId -> rows), as leadDecisions.decisionsByLead builds it (optional). */
export const leadOptedOut = (lead, decisions = null) => isOptedOut(lead)
  || (!!lead && !!decisions && typeof decisions.get === 'function' && (decisions.get(String(lead.id)) || []).some((d) => OPT_OUT_DECISIONS.has(String(d.decision))));

/** A candidate or lead is "No email" when any lead with their address is opted out. */
export const personOptedOut = (person, leads, decisions = null) => isOptedOut(person) || leadOptedOut(person, decisions)
  || leadsWithAddress(person && person.email, leads).some((l) => leadOptedOut(l, decisions));

/** The timeline note -- the subject and the outcome, who sent it, the lead by id. Never the address or the body. */
export function emailNote({ subject, status, by = '', leadId = null }) {
  const parts = [`Email · ${oneLine(subject) || 'No subject'}`, status];
  if (s(by)) parts.push(`by ${oneLine(by)}`);
  if (leadId && UUID.test(String(leadId))) parts.push(`lead_id=${leadId}`);
  return parts.join(' · ');
}
const NOTE_RE = /^Email · (.+?) · (Sent|Bounced|Opted out)(?: · by (.+?))?(?: · lead_id=([0-9a-f-]{36}))?$/i;
/** -> { subject, status, by, leadId } | null */
export function parseEmailNote(note) {
  const m = NOTE_RE.exec(s(note));
  return m ? { subject: m[1], status: m[2], by: m[3] || '', leadId: m[4] || null } : null;
}

/** Client-side checks that match the mailer's, so a refusal is said before anything is sent. -> error code | '' */
export function checkEmail({ to, kind, subject, body }) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s(to))) return 'bad_address';
  if (!BODY_MAX[kind]) return 'bad_kind';
  if (!s(body)) return 'empty_body';
  if (String(body).trim().length > BODY_MAX[kind]) return 'body_too_long';
  if (kind === 'message') {
    if (!s(subject)) return 'empty_subject';
    if (s(subject).length > SUBJECT_MAX) return 'subject_too_long';
  }
  return '';
}

/** Is the opt-out column there yet? (the hub applies 2026-10-22-leads-email.sql) */
export async function probeEmailColumns(client) {
  try { const { error } = await client.from('leads').select('email_opt_out,email_sent_at', { head: true }).limit(0); return !error; } catch { return false; }
}

/**
 * supabase + staff (supabase.js) -> the CRM's one email sender.
 *   send({ to, kind, subject, body, leadId?, candidateId?, by? }) -> { sent, messageId, logged }
 *     throws Error(.code) -- recipient_suppressed after logging Bounced and marking the address No email.
 *   setOptOut(leadIds, value, by) -> true when written; false when the column is not there yet.
 */
export function makeEmailApi({ supabase, staff }) {
  const whoAmI = async (by) => {
    if (s(by)) return s(by);
    try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.email) || ''; } catch { return ''; }
  };
  const log = async ({ subject, status, by, leadId, candidateId }) => {
    const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId || null, date: new Date().toISOString().split('T')[0],
      type: EMAIL_LOG_TYPE, note: emailNote({ subject, status, by, leadId }) });
    return !error;
  };
  const optOutAddress = async (address, by) => {
    try {
      const { data, error } = await supabase.from('leads').select('id,email').ilike('email', s(address).replace(/[\\%_]/g, (c) => `\\${c}`));
      if (error) return false;
      const ids = leadsWithAddress(address, data).map((l) => l.id);
      return ids.length ? api.setOptOut(ids, true, by) : false;
    } catch { return false; }
  };
  const api = {
    async send({ to, kind = 'message', subject, body, leadId = null, candidateId = null, by = '' }) {
      const subj = s(subject);
      const bad = checkEmail({ to, kind, subject: subj, body });
      if (bad) { const e = new Error(EMAIL_ERRORS[bad]); e.code = bad; throw e; }
      const who = await whoAmI(by);
      try {
        const r = await staff.sendEmail(kind, s(to), String(body), kind === 'message' ? subj : undefined);
        const logged = await log({ subject: subj, status: STATUS.sent, by: who, leadId, candidateId });
        return { ...(r || {}), sent: true, logged };
      } catch (e) {
        const code = (e && e.code) || 'send_failed';
        if (code === 'recipient_suppressed') {
          await log({ subject: subj, status: STATUS.bounced, by: who, leadId, candidateId });
          await optOutAddress(to, who);
        }
        const err = new Error(EMAIL_ERRORS[code] || (e && e.message) || EMAIL_ERRORS.send_failed);
        err.code = code;
        throw err;
      }
    },
    async setOptOut(leadIds, value = true, by = '') {
      const ids = (leadIds || []).filter(Boolean);
      if (!ids.length) return false;
      const { error } = await supabase.from('leads').update({ email_opt_out: value === true }).in('id', ids);
      if (error) return false;
      if (value === true) {
        const who = await whoAmI(by);
        for (const id of ids) await log({ subject: 'No email', status: STATUS.optedOut, by: who, leadId: id, candidateId: null });
      }
      return true;
    },
    log,
  };
  return api;
}
